/**
 * usePlateLane - a person plate's lane (Squad Mode stage 8): which instrument it holds for this body (`laneKindOf`: a
 * person of a kill-team with an Oath - life signs in Solo, the B-scope in Squad), whose plate it is (the actor's or the
 * target's, from the roster's two ids) and where the lane stands in this cell (`laneFor`: the free room first, at most
 * ~8% of the figure). Nothing outside the table, nor for anyone else.
 *
 * @returns the lane's kind, geometry and owner, or null
 */
import { useContext, useMemo } from 'react';
import { useKillTeamsContext, type KillTeamsValue } from '../../contexts/KillTeamsContext';
import RosterContext from '../../contexts/RosterContext';
import { laneFor, laneKindOf, type Lane, type LaneKind } from '../../engine/plate/lane';
import type { Frame } from '../../engine/plate/calloutLayout';
import type { Target } from '../../types';

export interface PlateLaneOf {
  kind: LaneKind;
  lane: Lane;
  /** The combatant whose plate this is. */
  id: string;
  k: KillTeamsValue;
}

/** The tags of the kill-teams that play an Oath (Squad Mode is theirs), one set per change. */
function useOathTags(k: KillTeamsValue | null): ReadonlySet<string> {
  const teams = k?.teams;
  return useMemo(
    () => new Set(Object.values(teams?.killTeams ?? {}).flatMap((kt) => (kt.oath ? [kt.tag] : []))),
    [teams],
  );
}

/** Whose plate this is: the actor's or the target's, from the roster's two ids. */
function useOwner(side: 'actor' | 'target'): string | null {
  const ids = useContext(RosterContext) as { actorId?: string | null; targetId?: string | null } | null;
  return (side === 'actor' ? ids?.actorId : ids?.targetId) ?? null;
}

export default function usePlateLane(
  target: Target,
  side: 'actor' | 'target',
  frame: Frame,
  cell: { w: number; h: number } | null,
): PlateLaneOf | null {
  const k = useKillTeamsContext();
  const id = useOwner(side);
  const oathTags = useOathTags(k);
  const kind = k ? laneKindOf(target, oathTags) : null;
  const lane = useMemo(() => (kind ? laneFor(frame, cell) : null), [kind, frame, cell]);
  return k && kind && lane && id ? { kind, lane, id, k } : null;
}
